import React, { useEffect, useMemo, useState } from "react";
import { Label, Title } from "../../tremor/Text";
import { BarSkeleton } from "./bar";
import { LineSkeleton } from "./line";
import { MetricSkeleton } from "./metric";
const getSkeleton = (id) => {
    switch (id) {
        case "metric":
            return MetricSkeleton;
        case "bar":
            return BarSkeleton;
        case "line":
            return LineSkeleton;
        case "area":
            return LineSkeleton;
        case "pie":
            return BarSkeleton;
        case "matrix":
            return BarSkeleton;
        case "table":
            return BarSkeleton;
        case "map":
            return BarSkeleton;
        case "sankey":
            return BarSkeleton;
        default:
            return BarSkeleton;
    }
};
const ProgressBar = ({ className }) => {
    const [progress, setProgress] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => {
            setProgress((prevProgress) => {
                if (prevProgress >= 99) {
                    clearInterval(interval);
                    return 99;
                }
                if (prevProgress >= 80) {
                    return Math.min(100, prevProgress + 1);
                }
                if (prevProgress >= 60) {
                    return Math.min(100, prevProgress + 2);
                }
                if (prevProgress >= 40) {
                    return Math.min(100, prevProgress + 3);
                }
                return prevProgress + 4;
            });
        }, 500);
        return () => {
            clearInterval(interval);
        };
    }, []);
    return (React.createElement("div", { className: "onvo-w-full onvo-h-2 onvo-rounded-md onvo-bg-black/10 dark:onvo-bg-white/10 " + className },
        React.createElement("div", { className: "onvo-h-full onvo-transition-all onvo-duration-1000 onvo-ease-linear onvo-rounded-md onvo-bg-blue-500", style: { width: progress + "%" } })));
};
export const ChartPlaceholder = ({ className, title, showProgress, type = "bar" }) => {
    const Skeleton = useMemo(() => getSkeleton(type), [type]);
    return (React.createElement("div", { className: "onvo-relative onvo-w-full onvo-flex onvo-flex-col onvo-h-full onvo-animate-pulse " +
            className },
        title && title.trim() !== "" ? (React.createElement(Title, { className: "onvo-mb-2" }, title)) : React.createElement("div", { className: "onvo-h-5 onvo-bg-black/10 onvo-rounded-md dark:onvo-bg-white/10 onvo-w-5/6 onvo-mb-2" }),
        showProgress ? React.createElement(ProgressBar, { className: "onvo-mb-4" }) : React.createElement("div", { className: "onvo-w-2/3 onvo-h-2 onvo-mb-4 onvo-bg-black/10 onvo-rounded-md dark:onvo-bg-white/10" }),
        React.createElement("div", { className: "onvo-flex-1 onvo-min-h-0 onvo-h-full onvo-items-end onvo-flex" },
            React.createElement(Skeleton, null)),
        React.createElement("span", { className: "onvo-sr-only" }, "Loading...")));
};
export const ChartLoader = ({ id, className }) => {
    return (React.createElement("div", { className: "onvo-mt-2 onvo-loader-question-modal onvo-relative onvo-flex onvo-flex-row onvo-gap-3 onvo-items-start onvo-justify-start onvo-w-full" },
        React.createElement("div", { className: "onvo-px-3 onvo-py-2 onvo-flex onvo-flex-row onvo-gap-2 onvo-items-end onvo-rounded-xl onvo-bg-black/5 dark:onvo-bg-white/10" },
            React.createElement("div", { role: "status" },
                React.createElement("svg", { "aria-hidden": "true", className: "onvo-w-4 onvo-h-4 onvo-text-gray-200 onvo-animate-spin dark:onvo-text-gray-600 onvo-fill-blue-600", viewBox: "0 0 100 101", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                    React.createElement("path", { d: "M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z", fill: "currentColor" }),
                    React.createElement("path", { d: "M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z", fill: "currentFill" }))),
            React.createElement(Label, null, "Thinking"),
            React.createElement("div", { className: "onvo-flex onvo-gap-1 onvo-mb-1" },
                React.createElement("div", { className: "onvo-w-1 onvo-h-1 onvo-rounded-full onvo-animate-pulse onvo-bg-slate-500" }),
                React.createElement("div", { className: "onvo-w-1 onvo-h-1 onvo-rounded-full onvo-animate-pulse onvo-bg-slate-500" }),
                React.createElement("div", { className: "onvo-w-1 onvo-h-1 onvo-rounded-full onvo-animate-pulse onvo-bg-slate-500" })))));
};
//# sourceMappingURL=ChartLoader.js.map